"use client";

import { useEffect, useMemo, useRef } from "react";
import { usePathname } from "next/navigation";

import {
  buildSEOAgentHref,
  getSEOIntentConfig,
  getSEOIntentConfigForSource,
  localizeSEOIntentConfig,
  type SEOIntent,
} from "@/lib/seo-intents";
import { scorePurchaseIntent } from "@/lib/purchase-intent-score";
import { safeSessionSet } from "@/lib/browser-storage";
import { resolveCustomerLanguage, type CustomerLanguage } from "@/lib/customer-language";

export interface SEOAgentBridgeProps {
  intent?: SEOIntent;
  sourcePath?: string;
  currentPage?: string;
  headline?: string;
  subhead?: string;
  ctaLabel?: string;
  className?: string;
  language?: CustomerLanguage;
  /**
   * Real follow-up questions seekers with this page's entity usually ask
   * (composed from governed domain data, rules 24/29 — questions, never facts).
   * Each ask enters the SAME bridge target with that exact question as the
   * seeker's opening prompt; this is one bridge surface, not extra offers
   * (rule 39), and the card keeps its single impression (rule 47).
   */
  asks?: readonly string[];
  /**
   * The card's "Ask your chart:" line and the opening prompt a click stores,
   * for a page whose intent copy does not fit it. Defaults to the intent's.
   * Like headline and subhead, it changes words only: the intent, the offer
   * and the report plan stay the intent's.
   */
  prompt?: string;
  /** The card's preview list, overridden on the same terms as `prompt`. */
  previewItems?: readonly string[];
}

interface BridgeCardProps {
  href: string;
  headline: string;
  subhead: string;
  config: ReturnType<typeof getSEOIntentConfig>;
  ctaLabel: string;
  onClick: () => void;
}

type BridgePayload = {
  source_path: string;
  intent: SEOIntent;
  page_category: string;
  offer_type: string;
  plan_id?: string;
  placement: "inline_agent_bridge";
  purchase_intent_score?: number;
  purchase_intent_band?: string;
  purchase_intent_reason?: string;
};

type BridgeClickPayload = BridgePayload & { target: string };

function trackBridgeViewed(payload: BridgePayload) {
  void import("@/lib/analytics-seo-bridge")
    .then(({ trackSEOAgentBridgeViewed }) => trackSEOAgentBridgeViewed(payload))
    .catch(() => {});
}

function trackBridgeClicked(payload: BridgeClickPayload) {
  void import("@/lib/analytics-seo-bridge")
    .then(({ trackSEOAgentBridgeClicked }) => trackSEOAgentBridgeClicked(payload))
    .catch(() => {});
}

function trackBridgeIntentCaptured(payload: {
  source_path: string;
  intent: SEOIntent;
  page_category: string;
  target: string;
  placement: "inline_agent_bridge";
}) {
  void import("@/lib/analytics-seo-bridge")
    .then(({ trackSEOIntentCaptured }) => trackSEOIntentCaptured(payload))
    .catch(() => {});
}

function currentPath(fallback?: string) {
  if (typeof window === "undefined") return fallback || "";
  return window.location.pathname || fallback || "";
}

export default function SEOAgentBridge({
  intent,
  sourcePath,
  currentPage,
  headline,
  subhead,
  ctaLabel,
  className = "",
  language,
  asks,
  prompt,
  previewItems,
}: SEOAgentBridgeProps) {
  const pathname = usePathname();
  const resolvedLanguage = resolveCustomerLanguage(language, pathname || sourcePath);
  const config = useMemo(() => {
    const sourceConfig = getSEOIntentConfigForSource(currentPage || sourcePath);
    const selectedConfig = (() => {
      if (intent) {
        const intentConfig = getSEOIntentConfig(intent);
        return sourceConfig.intent === intent ? sourceConfig : intentConfig;
      }
      return sourceConfig;
    })();
    return localizeSEOIntentConfig(selectedConfig, resolvedLanguage);
  }, [currentPage, intent, resolvedLanguage, sourcePath]);

  const resolvedSource = sourcePath || pathname || (currentPage ? `/${currentPage}` : "");
  const href = buildSEOAgentHref(config, resolvedSource || config.pageCategory, resolvedLanguage);
  const purchaseIntent = useMemo(
    () =>
      scorePurchaseIntent({
        intent: config.intent,
        sourcePath: resolvedSource || config.pageCategory,
        primaryOffer: config.primaryOffer,
      }),
    [config, resolvedSource]
  );
  const finalHeadline = headline || config.headline;
  const finalSubhead = subhead || config.subhead;
  const finalCtaLabel = ctaLabel || config.ctaLabel;
  const cardConfig = useMemo(
    () => ({
      ...config,
      prompt: prompt || config.prompt,
      previewItems: previewItems?.length ? [...previewItems] : config.previewItems,
    }),
    [config, prompt, previewItems]
  );
  const bridgeRef = useRef<HTMLElement | null>(null);
  const trackedViewRef = useRef(false);

  useEffect(() => {
    trackedViewRef.current = false;
    const node = bridgeRef.current;

    const trackVisibleView = () => {
      if (trackedViewRef.current) return;
      trackedViewRef.current = true;
      const path = currentPath(resolvedSource || config.pageCategory);
      trackBridgeViewed({
        source_path: path,
        intent: config.intent,
        page_category: config.pageCategory,
        offer_type: config.primaryOffer,
        plan_id: config.reportPlanId,
        placement: "inline_agent_bridge",
        purchase_intent_score: purchaseIntent.score,
        purchase_intent_band: purchaseIntent.band,
        purchase_intent_reason: purchaseIntent.reason,
      });
    };

    if (!node || typeof IntersectionObserver === "undefined") {
      trackVisibleView();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
          trackVisibleView();
          observer.disconnect();
        }
      },
      { threshold: [0.5] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [config, purchaseIntent, resolvedSource]);

  function handleClick(promptOverride?: string) {
    const path = currentPath(resolvedSource || config.pageCategory);
    try {
      safeSessionSet("seo_entry_intent", config.intent);
      safeSessionSet("seo_entry_path", path);
      safeSessionSet("seo_entry_page_category", config.pageCategory);
      safeSessionSet("seo_entry_primary_offer", config.primaryOffer);
      safeSessionSet("seo_entry_report_plan_id", config.reportPlanId);
      safeSessionSet("seo_entry_prompt", promptOverride ?? cardConfig.prompt);
      safeSessionSet("seo_entry_purchase_intent_score", String(purchaseIntent.score));
      safeSessionSet("seo_entry_purchase_intent_band", purchaseIntent.band);
      safeSessionSet("seo_entry_purchase_intent_reason", purchaseIntent.reason);
    } catch {
      // Analytics still records the click when storage is unavailable.
    }
    trackBridgeClicked({
      source_path: path,
      intent: config.intent,
      page_category: config.pageCategory,
      offer_type: config.primaryOffer,
      plan_id: config.reportPlanId,
      target: config.target,
      placement: "inline_agent_bridge",
      purchase_intent_score: purchaseIntent.score,
      purchase_intent_band: purchaseIntent.band,
      purchase_intent_reason: purchaseIntent.reason,
    });
    trackBridgeIntentCaptured({
      source_path: path,
      intent: config.intent,
      page_category: config.pageCategory,
      target: config.target,
      placement: "inline_agent_bridge",
    });
  }

  return (
    <section
      ref={bridgeRef}
      data-agent-bridge="inline"
      className={`mx-auto max-w-2xl px-4 sm:px-6 py-8 ${className}`}
    >
      <BridgeCard
        href={href}
        onClick={() => handleClick()}
        config={cardConfig}
        headline={finalHeadline}
        subhead={finalSubhead}
        ctaLabel={finalCtaLabel}
        language={resolvedLanguage}
      />
      {asks && asks.length > 0 ? (
        <BridgeAsks
          asks={asks}
          href={href}
          language={resolvedLanguage}
          onAskClick={(ask) => handleClick(ask)}
        />
      ) : null}
    </section>
  );
}

/**
 * Follow-up questions rendered OUTSIDE the card anchor (nested links are
 * invalid HTML). Same destination, same click telemetry; only the stored
 * opening prompt differs, so a reference lookup becomes the chat surface
 * that already out-earns reference pages ~10x (2026-08-18 GSC evidence).
 */
function BridgeAsks({
  asks,
  href,
  language,
  onAskClick,
}: {
  asks: readonly string[];
  href: string;
  language: CustomerLanguage;
  onAskClick: (ask: string) => void;
}) {
  return (
    <div className="mt-3" data-agent-bridge-asks="">
      <p className="text-caption font-semibold text-text-muted">
        {language === "hi"
          ? "Log apni kundali se aksar poochhte hain:"
          : "People usually ask their chart:"}
      </p>
      <ul className="mt-2 space-y-1.5">
        {asks.map((ask) => (
          <li key={ask}>
            <a
              href={href}
              onClick={() => onAskClick(ask)}
              className="flex min-h-11 items-center gap-2 rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-body-small text-text-primary no-underline transition hover:shadow-sm"
            >
              <span aria-hidden className="text-intent-brand">
                ›
              </span>
              <span>{ask}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BridgeCard({
  href,
  onClick,
  config,
  headline,
  subhead,
  ctaLabel,
  language,
}: BridgeCardProps & { language: CustomerLanguage }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="block rounded-xl p-5 sm:p-6 no-underline transition hover:shadow-md"
      style={{
        background:
          "linear-gradient(135deg, color-mix(in srgb, var(--ish-accent) 12%, transparent) 0%, color-mix(in srgb, var(--ish-accent) 4%, var(--ish-bg)) 100%)",
        border: "1px solid color-mix(in srgb, var(--ish-accent) 30%, transparent)",
      }}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <BridgeIcon />
        <BridgeCopy config={config} headline={headline} subhead={subhead} language={language} />
        <BridgeCta label={ctaLabel} />
      </div>
      {/* The two highest-converting search modifiers on the property, stated
          verbatim and truthfully ("free" 12.4% / "without login" 23.0% CTR,
          GSC 90d pre-cliff). "Unlimited" is deliberately absent: free chat is
          capped at 8 messages, so that claim would be false. */}
      <p className="mt-3 text-center text-caption font-medium text-text-muted">
        {language === "hi" ? "Bilkul free · bina login" : "Free · No login needed"}
      </p>
    </a>
  );
}

function BridgeIcon() {
  return (
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg text-[22px]"
      style={{ background: "var(--surface-brand-strong)", color: "#fff" }}
      aria-hidden
    >
      ॐ
    </div>
  );
}

function BridgeCopy({
  config,
  headline,
  subhead,
  language,
}: {
  config: BridgeCardProps["config"];
  headline: string;
  subhead: string;
  language: CustomerLanguage;
}) {
  return (
    <div className="min-w-0 flex-1">
      <p
        className="mb-1 text-[10px] font-semibold uppercase"
        style={{ color: "var(--intent-brand-text)", letterSpacing: "0.16em" }}
      >
        {config.eyebrow}
      </p>
      <p
        className="text-[16px] sm:text-[17px] font-bold leading-snug"
        style={{ color: "var(--ish-dark)" }}
      >
        {headline}
      </p>
      <p
        className="mt-1 text-body-small sm:text-body leading-relaxed"
        style={{ color: "var(--ish-text-muted)" }}
      >
        {subhead}
      </p>
      <p className="mt-3 text-caption font-medium" style={{ color: "var(--intent-brand-text)" }}>
        {language === "hi" ? "Apni kundali se poochhein:" : "Ask your chart:"} {config.prompt}
      </p>
      <BridgePreview config={config} />
    </div>
  );
}

function BridgePreview({ config }: { config: BridgeCardProps["config"] }) {
  return (
    <div
      className="mt-4 rounded-lg p-3"
      style={{
        background: "color-mix(in srgb, var(--ish-bg) 86%, var(--ish-accent) 14%)",
        border: "1px solid color-mix(in srgb, var(--ish-accent) 18%, transparent)",
      }}
    >
      <p className="text-caption font-semibold" style={{ color: "var(--ish-dark)" }}>
        {config.previewTitle}
      </p>
      <ul className="mt-2 space-y-1.5">
        {config.previewItems.map((item) => (
          <li
            key={item}
            className="flex gap-2 text-caption leading-snug"
            style={{ color: "var(--ish-text-muted)" }}
          >
            <span aria-hidden style={{ color: "var(--intent-brand-text)" }}>
              •
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BridgeCta({ label }: { label: string }) {
  return (
    <div
      className="shrink-0 self-start rounded-lg px-4 py-2.5 text-body-small font-semibold sm:self-center"
      style={{ background: "var(--surface-brand-strong)", color: "#fff" }}
    >
      {label}
    </div>
  );
}
